import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Filter, Loader2, Network, Layers, BookOpen } from 'lucide-react';
import { resolutionApi, type CanonicalEntityData } from '@/services/api';
import { ConceptTypeTree, type ConceptTypeNode } from '@/components/knowledge/catalogs/ConceptTypeTree';
import { ConceptTypeDetail } from '@/components/knowledge/catalogs/ConceptTypeDetail';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Tab = 'concepts' | 'pending' | 'axes';

interface AxisEntry {
  slug: string;
  display_name: string;
  domain_scope: string | null;
  description: string | null;
  instance_count: number;
  is_override: boolean;
}

// ---------------------------------------------------------------------------
// Axes Registry tab
// ---------------------------------------------------------------------------

function AxesRegistryTab() {
  const [axes, setAxes] = useState<AxisEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [domainFilter, setDomainFilter] = useState('');
  const [searchQ, setSearchQ] = useState('');

  useEffect(() => {
    setLoading(true);
    const params = domainFilter ? `?domain_scope=${encodeURIComponent(domainFilter)}&limit=500` : '?limit=500';
    fetch(`/api/v1/knowledge/axes${params}`)
      .then((r) => r.json())
      .then((data) => setAxes(Array.isArray(data) ? data : []))
      .catch(() => setAxes([]))
      .finally(() => setLoading(false));
  }, [domainFilter]);

  const domains = useMemo(() => {
    const s = new Set<string>();
    axes.forEach((a) => { if (a.domain_scope) s.add(a.domain_scope); });
    return Array.from(s).sort();
  }, [axes]);

  const filtered = useMemo(() => {
    if (!searchQ) return axes;
    const q = searchQ.toLowerCase();
    return axes.filter(
      (a) =>
        a.slug.includes(q) ||
        a.display_name.toLowerCase().includes(q) ||
        (a.description || '').toLowerCase().includes(q),
    );
  }, [axes, searchQ]);

  const domainColor: Record<string, string> = {
    commerce: 'bg-blue-100 text-blue-700',
    ad_creative: 'bg-purple-100 text-purple-700',
    cognition: 'bg-teal-100 text-teal-700',
  };

  return (
    <div className="flex flex-col h-full">
      {/* Filter bar */}
      <div className="flex items-center gap-3 px-6 py-3 border-b border-slate-100 bg-white flex-wrap">
        <Filter size={12} className="text-slate-400" />
        <input
          type="search"
          placeholder="Search slugs…"
          value={searchQ}
          onChange={(e) => setSearchQ(e.target.value)}
          className="text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1.5 w-48 focus:outline-none focus:ring-1 focus:ring-brand-teal"
        />
        <select
          value={domainFilter}
          onChange={(e) => setDomainFilter(e.target.value)}
          className="text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-teal"
        >
          <option value="">All domains</option>
          {domains.map((d) => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>
        {(domainFilter || searchQ) && (
          <button
            onClick={() => { setDomainFilter(''); setSearchQ(''); }}
            className="text-xs text-slate-500 hover:text-slate-700"
          >
            Clear
          </button>
        )}
        <span className="ml-auto text-xs text-slate-400">{filtered.length} axes</span>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto px-6 py-4">
        {loading ? (
          <div className="text-sm text-slate-400 text-center py-8">
            <Loader2 size={14} className="animate-spin inline mr-2" /> Loading axis registry…
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-sm text-slate-400 text-center py-8">
            No axes found.{' '}
            {!domainFilter && (
              <span>Run the SQL migration to populate <code>concept_type_categories_v</code>.</span>
            )}
          </div>
        ) : (
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="border-b border-slate-200 text-slate-500 text-left">
                <th className="pb-2 font-medium w-48">Slug</th>
                <th className="pb-2 font-medium w-48">Display name</th>
                <th className="pb-2 font-medium w-28">Domain</th>
                <th className="pb-2 font-medium w-16 text-right">Count</th>
                <th className="pb-2 font-medium w-16 text-center">Override</th>
                <th className="pb-2 font-medium">Description</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((ax) => (
                <tr key={ax.slug} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="py-2 pr-3 font-mono text-slate-700">{ax.slug}</td>
                  <td className="py-2 pr-3 text-slate-700">{ax.display_name}</td>
                  <td className="py-2 pr-3">
                    {ax.domain_scope ? (
                      <span
                        className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium ${domainColor[ax.domain_scope] ?? 'bg-slate-100 text-slate-600'}`}
                      >
                        {ax.domain_scope}
                      </span>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums text-slate-500">
                    {ax.instance_count}
                  </td>
                  <td className="py-2 pr-3 text-center">
                    {ax.is_override ? (
                      <span className="text-amber-600 font-semibold">✓</span>
                    ) : (
                      <span className="text-slate-200">—</span>
                    )}
                  </td>
                  <td className="py-2 text-slate-500 truncate max-w-xs">
                    {ax.description ?? ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page (3 tabs)
// ---------------------------------------------------------------------------

export function ConceptTypePage() {
  const [tab, setTab] = useState<Tab>('concepts');
  const [raw, setRaw] = useState<CanonicalEntityData[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [scopeFilter, setScopeFilter] = useState<string>('');
  const [minInstances, setMinInstances] = useState<number>(0);

  useEffect(() => {
    setLoading(true);
    resolutionApi
      .listCanonicals({ entity_type: 'concept_type', limit: 200 })
      .then((res) => setRaw(res.canonicals))
      .catch(() => setRaw([]))
      .finally(() => setLoading(false));
  }, []);

  // Build hierarchy + filter options.
  const { tree, byId, categories, scopes } = useMemo(() => {
    const nodesById = new Map<string, ConceptTypeNode>();
    for (const c of raw) {
      nodesById.set(c.id, { ...c, children: [], depth: 0 });
    }

    const orphans: ConceptTypeNode[] = [];
    for (const node of nodesById.values()) {
      const parentId =
        typeof node.canonical_data?.parent_type_id === 'string'
          ? (node.canonical_data.parent_type_id as string)
          : null;
      if (parentId && nodesById.has(parentId)) {
        nodesById.get(parentId)!.children.push(node);
      } else {
        orphans.push(node);
      }
    }

    const assignDepth = (n: ConceptTypeNode, d: number) => {
      n.depth = d;
      for (const c of n.children) assignDepth(c, d + 1);
    };
    orphans.forEach((r) => assignDepth(r, 0));
    nodesById.forEach((n) =>
      n.children.sort((a, b) => a.canonical_name.localeCompare(b.canonical_name)),
    );
    orphans.sort((a, b) => a.canonical_name.localeCompare(b.canonical_name));

    const cats = new Set<string>();
    const scps = new Set<string>();
    for (const n of nodesById.values()) {
      const data = (n.canonical_data ?? {}) as Record<string, unknown>;
      if (typeof data.type_category === 'string') cats.add(data.type_category);
      if (Array.isArray(data.taxonomy_scope)) {
        (data.taxonomy_scope as string[]).forEach((s) => scps.add(s));
      }
    }

    return {
      tree: orphans,
      byId: nodesById,
      categories: Array.from(cats).sort(),
      scopes: Array.from(scps).sort(),
    };
  }, [raw]);

  const pendingCount = raw.filter((c) => c.maturity_stage === 'candidate' || c.maturity_stage === 'emerging').length;

  const filteredTree = useMemo(() => {
    const source = tab === 'pending'
      ? raw
          .filter((c) => c.maturity_stage === 'candidate' || c.maturity_stage === 'emerging')
          .map((c) => ({ ...c, children: [], depth: 0 }) as ConceptTypeNode)
      : tree;

    if (!categoryFilter && !scopeFilter && minInstances === 0) return source;
    const passes = (n: ConceptTypeNode): boolean => {
      const data = (n.canonical_data ?? {}) as Record<string, unknown>;
      const category = typeof data.type_category === 'string' ? data.type_category : '';
      const scope = Array.isArray(data.taxonomy_scope)
        ? (data.taxonomy_scope as string[])
        : [];
      const count = typeof data.instance_count === 'number' ? (data.instance_count as number) : 0;
      if (categoryFilter && category !== categoryFilter) return false;
      if (scopeFilter && !scope.includes(scopeFilter)) return false;
      if (count < minInstances) return false;
      return true;
    };
    const clone = (n: ConceptTypeNode): ConceptTypeNode | null => {
      const children = n.children.map(clone).filter((c): c is ConceptTypeNode => !!c);
      if (!passes(n) && children.length === 0) return null;
      return { ...n, children };
    };
    return source.map(clone).filter((c): c is ConceptTypeNode => !!c);
  }, [tree, raw, tab, categoryFilter, scopeFilter, minInstances]);

  const selected = selectedId ? (byId.get(selectedId) ?? null) : null;

  const TABS: { id: Tab; label: string; icon: React.ReactNode; count?: number }[] = [
    { id: 'concepts', label: 'Concepts', icon: <Network size={14} />, count: raw.filter((c) => !['candidate','emerging'].includes(c.maturity_stage ?? '')).length },
    { id: 'pending', label: 'Pending', icon: <Loader2 size={14} />, count: pendingCount },
    { id: 'axes', label: 'Axes Registry', icon: <Layers size={14} /> },
  ];

  return (
    <div className="flex flex-col h-full bg-slate-50">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-white">
        <div className="flex items-center gap-3">
          <Link
            to="/knowledge/catalogs"
            className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
          >
            <ArrowLeft size={14} />
            Catalogs
          </Link>
          <div className="flex items-center gap-2">
            <BookOpen size={18} className="text-brand-teal" />
            <div>
              <h1 className="text-xl font-semibold text-navy-900">Concept Taxonomy</h1>
              <p className="text-sm text-slate-500 mt-0.5">
                {raw.length} ConceptType canonicals · IS-A hierarchy · Layer 1 axes
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-0 border-b border-slate-200 bg-white px-6">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm border-b-2 transition-colors ${
              tab === t.id
                ? 'border-brand-teal text-brand-teal font-medium'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {t.icon}
            {t.label}
            {t.count !== undefined && t.count > 0 && (
              <span className={`ml-1 text-xs px-1.5 py-0.5 rounded-full ${tab === t.id ? 'bg-teal-100 text-teal-700' : 'bg-slate-100 text-slate-500'}`}>
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Axes Registry tab — standalone */}
      {tab === 'axes' && <AxesRegistryTab />}

      {/* Concepts / Pending tabs — share filter bar + tree/detail layout */}
      {tab !== 'axes' && (
        <>
          <div className="flex items-center gap-3 px-6 py-3 border-b border-slate-100 bg-white">
            <Filter size={12} className="text-slate-400" />
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-teal"
            >
              <option value="">All categories</option>
              {categories.map((c) => (
                <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>
              ))}
            </select>
            <select
              value={scopeFilter}
              onChange={(e) => setScopeFilter(e.target.value)}
              className="text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-teal"
            >
              <option value="">All scopes</option>
              {scopes.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <label className="flex items-center gap-1 text-xs text-slate-500">
              Min instances
              <input
                type="number"
                min={0}
                step={1}
                value={minInstances}
                onChange={(e) => setMinInstances(Number(e.target.value || 0))}
                className="ml-1 w-16 text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-brand-teal"
              />
            </label>
            {(categoryFilter || scopeFilter || minInstances > 0) && (
              <button
                onClick={() => { setCategoryFilter(''); setScopeFilter(''); setMinInstances(0); }}
                className="text-xs text-slate-500 hover:text-slate-700"
              >
                Clear
              </button>
            )}
          </div>

          <div className="flex flex-1 min-h-0">
            <div className="w-[55%] p-4 overflow-auto">
              {loading && raw.length === 0 ? (
                <div className="text-sm text-slate-400 text-center py-8">
                  <Loader2 size={14} className="animate-spin inline mr-2" /> Loading concept types…
                </div>
              ) : (
                <ConceptTypeTree
                  roots={filteredTree}
                  selectedId={selectedId}
                  onSelect={(n) => setSelectedId(n.id)}
                />
              )}
            </div>
            <div className="flex-1 min-w-0 p-4 bg-white border-l border-slate-200 overflow-auto">
              <ConceptTypeDetail node={selected} byId={byId} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
